/**
 * Where a printed plan's words go: every room name, wall dimension, object tag
 * and caption placed so that no two of them overlap, none sits on a comment
 * pin or on a drawn object, and none runs off the drawing — with a thin
 * leader line back to its thing whenever a label has had to move away from it.
 *
 * GREEDY, IN PRIORITY ORDER. Pins and object bodies are fixed. Labels are then
 * placed one at a time, most tied-to-its-place first (a tag inside its unit, a
 * wall's figure on its dimension line, a room's name in its room), each at the
 * first of its own candidate positions that is clear of everything placed
 * before it. When none is, rings of positions further out are tried, and the
 * label gets a leader. Deterministic: the same plan places every label in the
 * same place, so the installer pack's hash stays stable.
 *
 * TEXT IS MEASURED, NOT GUESSED. The widths are Helvetica's advance widths as
 * Chromium — the engine that prints the PDFs — reports them for the plan's font
 * stack ("Helvetica, Arial, sans-serif"), measured by
 * docs/research/label-overlap-2026-09-11/measure-font.mjs. A box runs from
 * 0.75 em above the baseline (capitals) to 0.22 em below it (descenders).
 */

/** Plan-mm on paper. */
export interface Pt {
  readonly x: number;
  readonly y: number;
}

// ------------------------------------------------------------ text width --

/** Advance widths, thousandths of an em, for characters 32–126: regular, then bold. */
const REGULAR =
  "278,278,355,556,556,889,667,191,333,333,389,584,278,333,278,278,556,556,556,556,556,556,556,556,556,556,278,278,584,584,584,556,1015,667,667,722,722,667,611,778,722,278,500,667,556,833,722,778,667,778,722,667,611,722,667,944,667,667,611,278,278,278,469,556,333,556,556,500,556,556,278,556,556,222,222,500,222,833,556,556,556,556,333,500,278,556,500,722,500,500,500,334,260,334,584"
    .split(",")
    .map(Number);
const BOLD =
  "278,333,474,556,556,889,722,238,333,333,389,584,278,333,278,278,556,556,556,556,556,556,556,556,556,556,333,333,584,584,584,611,975,722,722,722,722,667,611,778,722,278,556,722,611,833,722,778,667,778,722,667,611,722,667,944,667,667,611,333,278,333,584,556,333,556,611,556,611,556,333,611,611,278,278,556,278,889,611,611,611,611,389,556,333,611,556,778,556,556,500,389,280,389,584"
    .split(",")
    .map(Number);
/** The non-ASCII characters a plan prints, same in both weights. */
const OTHER: Readonly<Record<string, number>> = { "×": 584, "²": 333, "Ø": 778, "–": 556, "—": 1000, "…": 1000, "£": 556, "·": 278 };

/** The printed width of `text` at `sizeMm`, mm. Weights 600 and up print in Helvetica Bold. */
export function textWidthMm(text: string, sizeMm: number, weight = 400): number {
  const table = weight >= 600 ? BOLD : REGULAR;
  let em = 0;
  for (const ch of text) {
    const code = ch.codePointAt(0)!;
    em += code >= 32 && code <= 126 ? table[code - 32]! : (OTHER[ch] ?? 1000);
  }
  return (em / 1000) * sizeMm;
}

/** How far a line's box reaches above and below its baseline, in ems. */
export const TEXT_ASCENT_EM = 0.75;
export const TEXT_DESCENT_EM = 0.22;

// -------------------------------------------------------------- geometry --

/** A rectangle turned about its centre: centre, half-width, half-height, angle (radians). */
export interface Obb {
  readonly cx: number;
  readonly cy: number;
  readonly hw: number;
  readonly hh: number;
  readonly angle: number;
}

export function obbCorners(b: Obb): Pt[] {
  const c = Math.cos(b.angle);
  const s = Math.sin(b.angle);
  return [
    [-b.hw, -b.hh],
    [b.hw, -b.hh],
    [b.hw, b.hh],
    [-b.hw, b.hh],
  ].map(([x, y]) => ({ x: b.cx + x! * c - y! * s, y: b.cy + x! * s + y! * c }));
}

function axesOf(b: Obb): Pt[] {
  const c = Math.cos(b.angle);
  const s = Math.sin(b.angle);
  return [
    { x: c, y: s },
    { x: -s, y: c },
  ];
}

function project(corners: readonly Pt[], axis: Pt): [number, number] {
  let lo = Infinity;
  let hi = -Infinity;
  for (const p of corners) {
    const v = p.x * axis.x + p.y * axis.y;
    if (v < lo) lo = v;
    if (v > hi) hi = v;
  }
  return [lo, hi];
}

/** Do two turned rectangles overlap (by more than `eps` on every axis)? Separating-axis test. */
export function obbOverlap(a: Obb, b: Obb, eps = 0): boolean {
  const ca = obbCorners(a);
  const cb = obbCorners(b);
  for (const axis of [...axesOf(a), ...axesOf(b)]) {
    const [a0, a1] = project(ca, axis);
    const [b0, b1] = project(cb, axis);
    if (Math.min(a1, b1) - Math.max(a0, b0) <= eps) return false;
  }
  return true;
}

/** The point of a turned rectangle nearest `p` (p itself when inside). */
export function obbNearest(b: Obb, p: Pt): Pt {
  const c = Math.cos(b.angle);
  const s = Math.sin(b.angle);
  const dx = p.x - b.cx;
  const dy = p.y - b.cy;
  const lx = Math.max(-b.hw, Math.min(b.hw, dx * c + dy * s));
  const ly = Math.max(-b.hh, Math.min(b.hh, -dx * s + dy * c));
  return { x: b.cx + lx * c - ly * s, y: b.cy + lx * s + ly * c };
}

export function obbCircle(b: Obb, centre: Pt, r: number): boolean {
  const q = obbNearest(b, centre);
  return Math.hypot(q.x - centre.x, q.y - centre.y) < r;
}

/** Does segment p–q cross (or lie in) a turned rectangle? */
function obbSegment(b: Obb, p: Pt, q: Pt): boolean {
  const c = Math.cos(b.angle);
  const s = Math.sin(b.angle);
  const local = (v: Pt) => ({ x: (v.x - b.cx) * c + (v.y - b.cy) * s, y: -(v.x - b.cx) * s + (v.y - b.cy) * c });
  const a = local(p);
  const d = local(q);
  // Liang–Barsky against the axis-aligned box in the rectangle's own frame.
  let t0 = 0;
  let t1 = 1;
  const dx = d.x - a.x;
  const dy = d.y - a.y;
  for (const [pp, qq] of [
    [-dx, a.x + b.hw],
    [dx, b.hw - a.x],
    [-dy, a.y + b.hh],
    [dy, b.hh - a.y],
  ] as const) {
    if (pp === 0) {
      if (qq < 0) return false;
    } else {
      const t = qq / pp;
      if (pp < 0) t0 = Math.max(t0, t);
      else t1 = Math.min(t1, t);
      if (t0 > t1) return false;
    }
  }
  return true;
}

function pointInPolygon(p: Pt, polygon: readonly Pt[]): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i, i += 1) {
    const a = polygon[i]!;
    const b = polygon[j]!;
    if (a.y > p.y !== b.y > p.y && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) inside = !inside;
  }
  return inside;
}

// ---------------------------------------------------------------- labels --

/** One line of a label: its text, and its baseline below the label's origin (mm). */
export interface LabelLine {
  readonly text: string;
  readonly sizeMm: number;
  readonly weight?: number;
  readonly dy: number;
  readonly fill: string;
  /** A white outline under the text, so it reads across a line. */
  readonly halo?: boolean;
}

/** A position a label may take: its origin (the centre of its lines' width, on paper), turned by `angleDeg`. */
export interface LabelSpot {
  readonly x: number;
  readonly y: number;
  readonly angleDeg?: number;
  /** Draw a leader back to the label's anchor from here. Default: only when the box is further than `leaderBeyondMm` from it. */
  readonly leader?: boolean;
}

export interface LabelRequest {
  readonly lines: readonly LabelLine[];
  /** Positions in the order they are wanted. Every one is tried before the rings further out. */
  readonly spots: readonly LabelSpot[];
  /** What the label names: the leader's far end, and the centre of the fallback rings. */
  readonly anchor: Pt;
  /** A leader starts this far out from the anchor, toward the label: on a round vessel's rim, not its middle. */
  readonly anchorRadiusMm?: number;
  /** Beyond this distance from the anchor, a label placed at one of its own spots gets a leader. Default: never. */
  readonly leaderBeyondMm?: number;
  /** Bodies this label may sit on — a unit's tag goes inside the unit. */
  readonly ownBodies?: readonly string[];
  /** The polygon a label's own spots must keep their centre inside (a room's floor, on paper). */
  readonly within?: readonly Pt[];
  /** Extra attributes on the <text>, e.g. `data-label="room"`. */
  readonly attrs?: string;
  /** What kind of label it is, for a renderer that files labels by kind — the DXF's layers. */
  readonly role?: string;
}

/** Something a label must not sit on: a pin's disc, or a drawn object's body. */
export type Obstacle =
  | { readonly kind: "circle"; readonly id: string; readonly c: Pt; readonly r: number }
  | { readonly kind: "box"; readonly id: string; readonly box: Obb };

export interface PlacedLabel {
  readonly request: LabelRequest;
  readonly origin: Pt;
  readonly angleDeg: number;
  /** The padded box it was placed by. */
  readonly box: Obb;
  /** The leader drawn, when it has one: anchor end first. */
  readonly leader: readonly [Pt, Pt] | null;
  /** False only when nowhere on the drawing was clear — the label is then at its first spot, overlapping. */
  readonly clear: boolean;
}

/**
 * Air kept round every label's box, mm: two labels are at least twice this
 * apart. More along a line than across it, so two captions side by side on
 * one baseline do not read as one run of words.
 */
export const LABEL_PAD_MM = 0.3;
export const LABEL_PAD_ALONG_MM = 0.8;
/** Air kept round a pin's disc, mm. */
const PIN_CLEARANCE_MM = 0.4;
/** A leader shorter than this is not drawn: the label is plainly beside its thing. */
const MIN_LEADER_MM = 1.2;

/** The label's box at `spot`, padded, on paper. */
export function labelBox(lines: readonly LabelLine[], spot: LabelSpot, pad = LABEL_PAD_MM, padAlong = pad === 0 ? 0 : LABEL_PAD_ALONG_MM): Obb {
  let w = 0;
  let top = Infinity;
  let bottom = -Infinity;
  for (const line of lines) {
    w = Math.max(w, textWidthMm(line.text, line.sizeMm, line.weight));
    top = Math.min(top, line.dy - TEXT_ASCENT_EM * line.sizeMm);
    bottom = Math.max(bottom, line.dy + TEXT_DESCENT_EM * line.sizeMm);
  }
  const angle = ((spot.angleDeg ?? 0) * Math.PI) / 180;
  const yc = (top + bottom) / 2;
  return {
    cx: spot.x - Math.sin(angle) * yc,
    cy: spot.y + Math.cos(angle) * yc,
    hw: w / 2 + padAlong,
    hh: (bottom - top) / 2 + pad,
    angle,
  };
}

/**
 * The spot that puts a label's (upright) box beside a thing, `gapMm` clear of
 * it in the direction `dir`: `reach` is how far the thing itself extends that
 * way from `from`.
 */
export function besideSpot(lines: readonly LabelLine[], from: Pt, dir: Pt, reach: number, gapMm: number): LabelSpot {
  const probe = labelBox(lines, { x: 0, y: 0 }, 0);
  const l = Math.hypot(dir.x, dir.y) || 1;
  const u = { x: dir.x / l, y: dir.y / l };
  // How far the box reaches along u from its own centre.
  const extent = Math.abs(u.x) * (probe.hw + LABEL_PAD_ALONG_MM) + Math.abs(u.y) * (probe.hh + LABEL_PAD_MM);
  const d = reach + gapMm + extent;
  // The origin sits `probe.cy` above the box's centre.
  return { x: from.x + u.x * d, y: from.y + u.y * d - probe.cy };
}

/** Fallback rings: directions tried at each radius, below and above first, as a caption is read. */
const RING_DIRECTIONS = [90, 270, 0, 180, 45, 135, 315, 225, 67.5, 112.5, 292.5, 247.5, 22.5, 157.5, 337.5, 202.5];
const RING_GAPS_MM = [1.5, 4, 7, 11, 16, 22, 30, 40, 55];

export interface LayoutFrame {
  /** The drawing's extent, paper mm: labels stay inside it. */
  readonly width: number;
  readonly height: number;
  /** Walls on paper. A label may cross one (its halo keeps it readable) but a clear spot is preferred. */
  readonly walls?: readonly (readonly [Pt, Pt])[];
}

/**
 * Places `requests`, in order, clear of `obstacles` and of each other. The
 * result is index for index with `requests`.
 */
export function layoutLabels(
  requests: readonly LabelRequest[],
  obstacles: readonly Obstacle[],
  frame: LayoutFrame,
): PlacedLabel[] {
  const placed: PlacedLabel[] = [];
  const leaders: [Pt, Pt][] = [];

  const inside = (box: Obb) =>
    obbCorners(box).every((p) => p.x >= 0.4 && p.y >= 0.4 && p.x <= frame.width - 0.4 && p.y <= frame.height - 0.4);
  const hitsObstacle = (box: Obb, own: readonly string[]) =>
    obstacles.some((o) =>
      own.includes(o.id)
        ? false
        : o.kind === "circle"
          ? obbCircle(box, o.c, o.r + (o.id.startsWith("pin:") ? PIN_CLEARANCE_MM : 0))
          : obbOverlap(box, o.box),
    );
  const hitsLabel = (box: Obb) => placed.some((p) => obbOverlap(box, p.box));
  /** Soft: leaders already drawn that this box would sit on, and placed labels this leader would cross. */
  const crossings = (box: Obb, leader: readonly [Pt, Pt] | null) =>
    leaders.filter(([a, b]) => obbSegment(box, a, b)).length +
    (leader ? placed.filter((p) => obbSegment(p.box, leader[0], leader[1])).length : 0);

  /** Soft: the wall lines a box would sit on (its halo would hide them). */
  const walls = (box: Obb) => (frame.walls ?? []).filter(([a, b]) => obbSegment(box, a, b)).length;

  const leaderFor = (request: LabelRequest, box: Obb, forced: boolean | undefined): [Pt, Pt] | null => {
    const end = obbNearest(box, request.anchor);
    const d = Math.hypot(end.x - request.anchor.x, end.y - request.anchor.y);
    const wanted = forced ?? (request.leaderBeyondMm !== undefined && d > request.leaderBeyondMm);
    const r = Math.min(request.anchorRadiusMm ?? 0, d);
    const from = d > 0 ? { x: request.anchor.x + ((end.x - request.anchor.x) / d) * r, y: request.anchor.y + ((end.y - request.anchor.y) / d) * r } : request.anchor;
    return wanted && d - r >= MIN_LEADER_MM ? [from, end] : null;
  };

  interface Choice {
    readonly spot: LabelSpot;
    readonly box: Obb;
    readonly leader: [Pt, Pt] | null;
    readonly score: number;
  }

  for (const request of requests) {
    const own = request.ownBodies ?? [];
    const found: { best: Choice | null } = { best: null };

    const consider = (wanted: LabelSpot, rank: number, forcedLeader: boolean | undefined) => {
      let spot = wanted;
      let box = labelBox(request.lines, spot);
      if (!inside(box) && !spot.angleDeg) {
        // An upright label running off the drawing's edge — a long model name
        // under a unit near the edge — is slid back on rather than given up.
        const corners = obbCorners(box);
        const lo = (v: number[]) => Math.min(...v);
        const hi = (v: number[]) => Math.max(...v);
        const xs = corners.map((p) => p.x);
        const ys = corners.map((p) => p.y);
        const dx = Math.max(0, 0.4 - lo(xs)) - Math.max(0, hi(xs) - (frame.width - 0.4));
        const dy = Math.max(0, 0.4 - lo(ys)) - Math.max(0, hi(ys) - (frame.height - 0.4));
        spot = { ...spot, x: spot.x + dx, y: spot.y + dy };
        box = labelBox(request.lines, spot);
        rank += 0.5;
      }
      if (!inside(box) || hitsObstacle(box, own) || hitsLabel(box)) return;
      const leader = leaderFor(request, box, forcedLeader);
      // A leader that would run over a pin is no good either — unless the pin
      // is on the very thing the leader comes from.
      if (
        leader &&
        obstacles.some(
          (o) =>
            o.kind === "circle" &&
            o.id.startsWith("pin:") &&
            Math.hypot(o.c.x - request.anchor.x, o.c.y - request.anchor.y) > o.r &&
            segmentNearCircle(leader, o.c, o.r),
        )
      ) {
        return;
      }
      const score = rank + 40 * crossings(box, leader) + 3 * walls(box);
      if (!found.best || score < found.best.score) found.best = { spot, box, leader, score };
    };

    request.spots.forEach((spot, i) => {
      if (request.within && i > 0 && !pointInPolygon(labelCentre(request.lines, spot), request.within)) return;
      consider(spot, i, spot.leader);
    });
    if (!found.best) {
      // Nowhere of its own is clear: rings further out, upright, with a leader home.
      let rank = 1000;
      for (const gap of RING_GAPS_MM) {
        for (const deg of RING_DIRECTIONS) {
          const a = (deg * Math.PI) / 180;
          consider(besideSpot(request.lines, request.anchor, { x: Math.cos(a), y: Math.sin(a) }, 0, gap), rank, true);
          rank += 1;
        }
        // The nearest ring with anything clear on it.
        if (found.best) break;
      }
    }

    const best = found.best;
    if (best) {
      placed.push({ request, origin: { x: best.spot.x, y: best.spot.y }, angleDeg: best.spot.angleDeg ?? 0, box: best.box, leader: best.leader, clear: true });
      if (best.leader) leaders.push(best.leader);
    } else {
      const spot = request.spots[0]!;
      placed.push({ request, origin: { x: spot.x, y: spot.y }, angleDeg: spot.angleDeg ?? 0, box: labelBox(request.lines, spot), leader: null, clear: false });
    }
  }
  return placed;
}

function labelCentre(lines: readonly LabelLine[], spot: LabelSpot): Pt {
  const b = labelBox(lines, spot, 0);
  return { x: b.cx, y: b.cy };
}

function segmentNearCircle([p, q]: readonly [Pt, Pt], c: Pt, r: number): boolean {
  const dx = q.x - p.x;
  const dy = q.y - p.y;
  const t = Math.max(0, Math.min(1, ((c.x - p.x) * dx + (c.y - p.y) * dy) / (dx * dx + dy * dy || 1)));
  return Math.hypot(p.x + dx * t - c.x, p.y + dy * t - c.y) < r;
}
